import React, { useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { Stars } from './ReputationBadge';

/**
 * The scorecard, on a collaboration that has closed.
 *
 * Two rules from the API are surfaced here rather than only enforced there,
 * because a rule the user discovers by being rejected is a bad rule:
 *
 * * **Double-blind.** The other side's verdict stays hidden until both are in
 *   or the blind window lapses. The panel says which of those is happening —
 *   "they have not rated you" and "they have, sealed" are very different
 *   messages, and conflating them is what makes people suspect the platform
 *   is hiding something.
 * * **A low score has to be explained.** The comment box becomes required
 *   below 3★ before the request is sent, so nobody writes a one-star review,
 *   hits send and gets a 400 back.
 *
 * Once published a rating is frozen — it is part of someone else's public
 * record by then — so the edit affordance disappears rather than failing.
 */

function CriterionRow({ label, value, onChange }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1.5">
            <span className="text-xs text-slate-600 flex-1 min-w-0">{label}</span>
            <Stars value={value || 0} size="w-5 h-5" interactive onPick={onChange} />
        </div>
    );
}

function RatingForm({ dealId, state, onDone }) {
    const { t } = useTranslation();
    const [overall, setOverall] = useState(state.mine?.overall || 0);
    const [scores, setScores] = useState(state.mine?.scores || {});
    const [comment, setComment] = useState(state.mine?.comment || '');
    const [again, setAgain] = useState(state.mine?.would_work_again ?? true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Mirrors the server rule so the requirement shows up while typing rather
    // than as a rejection after sending.
    const needsComment = overall > 0 && overall <= 2 && !comment.trim();

    const submit = async () => {
        if (!overall) { setError(t('reputation.pickOverall')); return; }
        if (needsComment) { setError(t('reputation.commentRequired')); return; }

        setBusy(true);
        setError('');
        try {
            const res = await api.post(`/api/campaigns/deals/${dealId}/rate/`, {
                overall, scores, comment, would_work_again: again,
            });
            if (res.status === 200 || res.status === 201) onDone(res.data);
        } catch (err) {
            const code = err.response?.data?.error;
            setError(code === 'comment_required' ? t('reputation.commentRequired')
                : code === 'window_closed' ? t('reputation.windowClosed')
                    : code === 'already_published' ? t('reputation.alreadyPublished')
                        : t('campaigns.saveError'));
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-3">
            <div className="text-center py-1">
                <p className="text-xs text-slate-500 mb-1.5">
                    {t('reputation.overallQuestion')} <strong className="text-slate-700">{state.counterparty}</strong>
                </p>
                <div className="flex justify-center">
                    <Stars value={overall} size="w-8 h-8" interactive onPick={setOverall} />
                </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 px-3 py-1.5 divide-y divide-slate-50">
                {state.criteria_keys.map(key => (
                    <CriterionRow key={key} label={t(`reputation.criterion_${key}`)}
                        value={scores[key]}
                        onChange={(value) => setScores(prev => ({ ...prev, [key]: value }))} />
                ))}
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={again} onChange={(e) => setAgain(e.target.checked)}
                    className="w-4 h-4 accent-[#20c9b5]" />
                <span className="text-xs text-slate-600">{t('reputation.wouldWorkAgainQuestion')}</span>
            </label>

            <div>
                <textarea className="input bg-white text-sm min-h-[70px] resize-y" value={comment}
                    placeholder={needsComment
                        ? t('reputation.commentPlaceholderRequired')
                        : t('reputation.commentPlaceholder')}
                    onChange={(e) => setComment(e.target.value)} />
                {needsComment && (
                    <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                        {t('reputation.commentRequiredHint')}
                    </p>
                )}
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}

            <button onClick={submit} disabled={busy || !overall}
                className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-40">
                {busy && <span className="spinner-sm" />}
                {state.mine ? t('reputation.updateBtn') : t('reputation.submitBtn')}
            </button>

            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                {t('reputation.blindNote').replace('{days}', state.blind_days)}
            </p>
        </div>
    );
}

function GivenRating({ rating, sealed, t }) {
    return (
        <div className={`rounded-xl border p-3 ${sealed
            ? 'border-slate-200 bg-slate-50'
            : 'border-emerald-200 bg-emerald-50/50'}`}>
            <div className="flex items-center gap-2 mb-1">
                <Stars value={rating.overall} size="w-4 h-4" />
                <span className="text-xs font-bold text-slate-700 tabular-nums">{rating.overall}/5</span>
                {sealed && (
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold">
                        {t('reputation.sealed')}
                    </span>
                )}
            </div>
            {rating.comment && (
                <p className="text-sm text-slate-600 leading-relaxed">"{rating.comment}"</p>
            )}
        </div>
    );
}

function DealRating({ deal, onUpdated }) {
    const { t } = useTranslation();
    const [state, setState] = useState(deal.rating);
    const [open, setOpen] = useState(false);

    if (!state) return null;

    // Nothing to say about a collaboration that has not finished.
    if (!state.can_rate && !state.mine && !state.theirs) {
        if (state.window_closed) {
            return (
                <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                    {t('reputation.windowClosed')}
                </p>
            );
        }
        return null;
    }

    const done = Boolean(state.mine);

    return (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
            <button onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-slate-100/70 transition">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-500 flex-shrink-0"
                    fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800">
                        {done ? t('reputation.panelTitleDone') : t('reputation.panelTitle')}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">
                        {done
                            ? (state.theirs ? t('reputation.bothIn')
                                : state.theirs_pending ? t('reputation.theirsSealed')
                                    : t('reputation.awaitingThem'))
                            : t('reputation.panelHint')}
                    </p>
                </div>
                {!done && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold flex-shrink-0">
                        {t('reputation.todo')}
                    </span>
                )}
                <svg xmlns="http://www.w3.org/2000/svg"
                    className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-slate-200 pt-3">
                    {/* Mine */}
                    {state.mine && (
                        <div>
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                                {t('reputation.yourRating')}
                            </p>
                            <GivenRating rating={state.mine} sealed={!state.mine.revealed} t={t} />
                            {!state.mine.revealed && (
                                <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">
                                    {t('reputation.editableWhileSealed')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Theirs */}
                    {state.theirs && (
                        <div>
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                                {t('reputation.theirRating')}
                            </p>
                            <GivenRating rating={state.theirs} sealed={false} t={t} />
                        </div>
                    )}
                    {state.theirs_pending && !state.theirs && (
                        <p className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2 leading-relaxed">
                            {t('reputation.theirsSealedNote')}
                        </p>
                    )}

                    {/* Form — hidden once the rating is public and frozen. */}
                    {state.can_rate && (!state.mine || !state.mine.revealed) && (
                        <RatingForm dealId={deal.id} state={state}
                            onDone={(data) => {
                                setState(data);
                                if (onUpdated) onUpdated();
                            }} />
                    )}
                </div>
            )}
        </div>
    );
}

export default DealRating;
